import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CategorySelect } from "@/components/CategorySelect";
import { UploadSimple, DownloadSimple, ArrowLeft, Sparkle, Trash, Warning } from "@phosphor-icons/react";

export function ImportDialog({
  importOpen, setImportOpen, importFile, setImportFile, importing, previewImport,
  importRows, setImportRow, removeImportRow, commitImport, importCommitting, resetImport,
  importSaveToLib, setImportSaveToLib,
  categories = [], money, benchmarks = {}, downloadTemplate, exportBudgetCsv,
}) {
  const reviewing = Array.isArray(importRows);
  const total = reviewing ? importRows.reduce((s, r) => s + (Number(r.budget) || 0), 0) : 0;
  const close = (open) => { setImportOpen(open); if (!open) resetImport(); };
  const bmFor = (r) => benchmarks[(r.category || "").trim().toLowerCase()];
  const overPct = (r) => {
    const b = bmFor(r);
    if (b && b.count >= 2 && Number(r.budget) > b.avg * 1.4) return Math.round((Number(r.budget) / b.avg - 1) * 100);
    return null;
  };

  return (
    <Dialog open={importOpen} onOpenChange={close}>
      <DialogContent className={reviewing ? "rounded-none max-w-4xl" : "rounded-none"}>
        <DialogHeader>
          <DialogTitle className="font-heading">{reviewing ? "Review Imported Items" : "Import Cost Items"}</DialogTitle>
        </DialogHeader>

        {!reviewing ? (
          <div className="space-y-4" data-testid="import-dialog">
            <p className="text-sm text-muted-foreground">
              Upload a <strong>CSV</strong> or <strong>Excel (.xlsx / .xls)</strong> file with columns:{" "}
              <span className="font-mono-fig text-xs">phase, category, description, vendor, budget, actual, status</span>.
              Unknown phases and vendors are created automatically, and category labels are auto-matched to your Hard/Soft taxonomy — you'll review before anything is saved.
            </p>
            <button onClick={downloadTemplate} data-testid="download-template" className="text-sm text-primary underline underline-offset-4 flex items-center gap-1">
              <DownloadSimple size={14} /> Download CSV template
            </button>
            {exportBudgetCsv && (
              <button onClick={exportBudgetCsv} data-testid="export-budget-csv" className="text-sm text-primary underline underline-offset-4 flex items-center gap-1">
                <DownloadSimple size={14} /> Export current budget to edit &amp; re-import
              </button>
            )}
            <div>
              <Label className="label-caps">File</Label>
              <Input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                data-testid="import-file-input" className="mt-2 rounded-none" />
            </div>
            <DialogFooter>
              <Button onClick={previewImport} disabled={importing || !importFile} data-testid="import-preview-button" className="rounded-none">
                <UploadSimple size={16} className="mr-1" />
                {importing ? "Parsing…" : "Preview & Review"}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3" data-testid="import-review">
            <p className="text-sm text-muted-foreground">
              {importRows.length} row{importRows.length === 1 ? "" : "s"} parsed. Categories were auto-matched to your taxonomy where possible
              (<Sparkle size={12} weight="fill" className="inline text-amber-500" /> = auto-matched). Adjust any before importing.
            </p>
            <div className="max-h-[52vh] overflow-y-auto border border-border">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-secondary text-[11px] label-caps sticky top-0">
                <span className="col-span-4">Description</span>
                <span className="col-span-5">Category</span>
                <span className="col-span-2 text-right">Budget</span>
                <span className="col-span-1"></span>
              </div>
              {importRows.map((r, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-2 items-center border-t border-border" data-testid={`import-row-${idx}`}>
                  <div className="col-span-4 min-w-0">
                    <span className="text-sm truncate block">{r.description || <span className="text-muted-foreground">—</span>}</span>
                    {r.phase && <span className="text-[11px] text-muted-foreground truncate block">{r.phase}</span>}
                  </div>
                  <div className="col-span-5 flex items-center gap-1.5">
                    {r.auto_matched && (
                      <Sparkle size={13} weight="fill" className="text-amber-500 shrink-0"
                        title={`Auto-matched from "${r.original_category}"`} data-testid={`import-automatch-${idx}`} />
                    )}
                    <CategorySelect categories={categories} value={r.category || ""}
                      onChange={(v) => setImportRow(idx, "category", v)}
                      placeholder={r.original_category || "Select category"}
                      className="rounded-none h-8 text-xs" testId={`import-category-${idx}`} />
                  </div>
                  <Input type="number" value={r.budget} onChange={(e) => setImportRow(idx, "budget", e.target.value)}
                    className="col-span-2 rounded-none h-8 text-xs text-right font-mono-fig" data-testid={`import-budget-${idx}`} />
                  <button onClick={() => removeImportRow(idx)} className="col-span-1 text-muted-foreground hover:text-rose-600 flex justify-center" data-testid={`import-remove-${idx}`}>
                    <Trash size={14} />
                  </button>
                  {(bmFor(r) || overPct(r) !== null) && (
                    <div className="col-span-12 flex items-center gap-2 -mt-1 pl-1">
                      {bmFor(r) && (
                        <span className="text-[11px] text-muted-foreground" data-testid={`import-benchmark-${idx}`}>
                          seen {bmFor(r).count}× · avg {money ? money(bmFor(r).avg) : bmFor(r).avg}
                        </span>
                      )}
                      {overPct(r) !== null && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-rose-600 border border-rose-300 px-1.5 py-0.5" data-testid={`import-overprice-${idx}`}>
                          <Warning size={11} weight="fill" /> {overPct(r)}% above avg
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Total budget</span>
              <span className="font-mono-fig font-semibold" data-testid="import-total">{money ? money(total) : total}</span>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="import-save-lib-label">
              <input type="checkbox" checked={!!importSaveToLib} onChange={(e) => setImportSaveToLib(e.target.checked)}
                data-testid="import-save-lib-checkbox" className="accent-primary" />
              Also save these items to my Cost Library (enriches pricing benchmarks)
            </label>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={resetImport} data-testid="import-back-button" className="rounded-none">
                <ArrowLeft size={16} className="mr-1" /> Back
              </Button>
              <Button onClick={commitImport} disabled={importCommitting || importRows.length === 0} data-testid="import-commit-button" className="rounded-none">
                <UploadSimple size={16} className="mr-1" />
                {importCommitting ? "Importing…" : `Import ${importRows.length} item${importRows.length === 1 ? "" : "s"}`}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
